import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PlusIcon from '@/assets/images/icons/plus_circle.svg';
import ExpandIcon from '@/assets/images/icons/minus-circle.svg';
import { getCategories, setLoading } from '@/store/actions/category';
import './style.scss';
import { getLogoFromFiles } from '@/common/utils';
import MyImage from '../../common/image';
import HeadingCenter from '@/components/common/heading-center';
import { categoryPage } from '@/components/common/heading-center/breadcrumb-config';
import IconFlag, { ICON } from '../../common/icon/iconFlag';
import { useFetchReferenceData } from '../utils';
import ActiveStatus from '@/components/common/activeStatus';
import Select from '@/components/common/select';
import { getApplications } from '@/store/actions/application';

import CreateCategory from '@/components/category/modal-create';
import NestedSortable from '@/components/common/nestedSortable';
import { MenuOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { requestPatchCategory } from '@/store/requests/category';
import Notification from '@/components/common/notification';
import { showMessageFailed } from '@/utils';
import FilterView from '@/components/common/filterView';

const CategoryList = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const stateCategory = useSelector((state) => state.category);
  const applications = useSelector((state) => state.application.list);
  const applicationOptions = useMemo(
    () => applications.map((_) => ({ label: _.name, value: _.guid })),
    [applications],
  );
  const [isExpandAll, setExpandAll] = useState(false);
  const [data, setData] = useState([]);
  const [queryData, setQuery] = useState({});
  const [initParentId, setInitParentId] = useState(null);
  const { list: categoryList, isLoading } = stateCategory;
  const [search, setSearch] = useState('');

  const [isModalVisible, setIsModalVisible] = useState(false);

  useFetchReferenceData();

  const showModal = () => {
    setIsModalVisible(true);
  };

  const getCategoriesFunc = (params = queryData) => {
    const queryParams = { ...params };
    dispatch(getCategories(queryParams));
    setQuery(queryParams);
  };

  useEffect(() => {
    getCategoriesFunc();
    dispatch(getApplications());
  }, []);

  useEffect(() => {
    const updateDataLoading = (nData) => {
      setData([...nData]);
    };

    const onSearch = (value = '') => {
      if (!value) {
        setExpandAll(false);
        return updateDataLoading(categoryList.data);
      }
      setExpandAll(true);
      const getData = _recursiveData(categoryList.data, value);
      return updateDataLoading(getData);
    };
    onSearch(search);
  }, [categoryList, search]);

  const dropItem = async (old, next, selected) => {
    const findNodeById = (nodes, key) => {
      let res;

      function findNode(nodes, key, parent = null) {
        for (let i = 0; i < nodes.length; i++) {
          if (nodes[i]?.id === key) {
            res = { data: nodes[i], index: i, parent, previous: nodes[i - 1] };
            break;
          }
          if (nodes[i]?.child_categories) {
            findNode(nodes[i]?.child_categories, key, nodes[i]?.id);
          }
        }
      }

      findNode(nodes, key);
      return res;
    };
    const oldPosition = findNodeById(old, +selected);
    const newPosition = findNodeById(next, +selected);
    setData(next);
    if (
      oldPosition &&
      newPosition &&
      (oldPosition?.index !== newPosition?.index || oldPosition?.parent !== newPosition?.parent)
    ) {
      const display_priority = newPosition?.previous?.display_priority;
      try {
        dispatch(setLoading(true));
        const res = await requestPatchCategory(selected, {
          display_priority:
            display_priority >= 0
              ? display_priority +
                (oldPosition?.parent !== newPosition?.parent
                  ? 1
                  : newPosition?.index - oldPosition?.index > 0
                  ? 0
                  : 1)
              : 0,
          parent_id: newPosition?.parent,
        });
        getCategoriesFunc();
        Notification({ type: 'success', message: res?.message || 'You have updated successfully' });
      } catch (error) {
        setData(old);
        dispatch(setLoading(false));
        showMessageFailed({ error });
      }
    }
  };

  const renderItem = (item, index, isExpand, setIsExpand, level) => {
    const classLvChild = level > 1 ? ' lv-child' : '';
    return (
      <div className="category-sort-item">
        <div className="icons-container sortable-view">
          <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />
        </div>
        <div className="icons-container">
          <div className="item-color default" style={{ background: item?.colour || '' }} />
        </div>
        <div className={`item-container-cate${classLvChild} cate-info`}>
          <div className="flex align-center cate-name-wrapper" style={{ '--level': level }}>
            {item.child_categories ? (
              <>
                <img
                  src={isExpand ? ExpandIcon : PlusIcon}
                  className="icon"
                  alt="edit icon"
                  onClick={() => setIsExpand(!isExpand)}
                />
              </>
            ) : (
              <div className="icon-space" />
            )}
            <MyImage
              src={getLogoFromFiles(item?.files)}
              width={50}
              height={50}
              className="cursor-default"
            />
            <div className="item-style cate-name">{item?.name}</div>
            <div className="cate-active">
              <ActiveStatus isActive={item?.is_active} />
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            setInitParentId(item?.id);
            showModal();
          }}
          className="ant-btn base-table-add-button sort-item-hide add-btn"
        >
          <IconFlag vector={ICON.ICONS_PLUS} />
        </button>
        <button
          onClick={() => history.push(`/category/${item?.id}`)}
          className="ant-btn base-table-add-button sort-item-hide add-btn"
        >
          <IconFlag vector={ICON.ICONS_EDIT_FILLED} />
        </button>
      </div>
    );
  };

  const moveItem = (e, dragItem) => {
    const setAtt = (item) => {
      const el = item.getElementsByClassName('cate-info');
      if (el?.length && el[0].children.length) {
        el[0].children[0].setAttribute(
          'style',
          `--level: ${e?.to?.parentElement?.dataset?.level || 0}`,
        );
      }
    };
    setAtt(e.item);
    setAtt(dragItem);
  };

  const _recursiveData = (newData = [], strSearch = null) => {
    const recurData = [];
    newData.forEach((el) => {
      let reData = [];
      if (el?.child_categories?.length) {
        reData = _recursiveData(el.child_categories, strSearch);
      }
      const elementData = { ...el, child_categories: reData?.length ? reData : undefined };
      if (
        (strSearch && el.name.toLowerCase().indexOf((strSearch || '').toLowerCase()) !== -1) ||
        !strSearch ||
        reData.length
      ) {
        recurData.push(elementData);
      }
    });
    return recurData;
  };

  return (
    <>
      <HeadingCenter pageConfig={categoryPage} isSearch>
        <CreateCategory
          onClose={() => {
            setInitParentId(null);
            setIsModalVisible(false);
          }}
          initParentId={initParentId}
          isOpen={isModalVisible}
        />
      </HeadingCenter>

      <div className="content-view">
        <FilterView
          isPagination={false}
          filter={
            <>
              <Select
                isPagination={false}
                className="filter-option"
                placeholder="All categories"
                options={applicationOptions}
                value={queryData.emulate_application}
                onChange={(v) => getCategoriesFunc({ ...queryData, emulate_application: v })}
                allowClear
              />
            </>
          }
          metadata={queryData}
          onChangePageProps={setQuery}
          onRemoveFilter={getCategoriesFunc}
          toggleExpandAllProp={setExpandAll}
          onSearch={setSearch}
        />
        <div className="category-table-header">
          <div />
          <div />
          <div>Category Name</div>
          <div />
          <div />
        </div>

        <NestedSortable
          isLoading={isLoading}
          data={data || []}
          child="child_categories"
          isExpandAll={isExpandAll}
          onChange={dropItem}
          renderItem={renderItem}
          onMove={moveItem}
        />
      </div>
    </>
  );
};

export default CategoryList;
